import { useQuery } from '@tanstack/react-query'

import request from './request'
import {
  zodMeResponseValidator,
  zodOrgUserResponseValidator,
  zodUserAssignmentResponseValidator,
  zodUserAuthIdentityValidator,
  zodUserResponseValidator,
  type AuthIdentityProvidersType,
  type MeResponse,
  type OrgUserResponse,
  type UserAssignmentResponse,
  type UserAuthIdentity,
  type UserResponse
} from './types/User'
import { useCurrentOrgId } from '@/stores/authStore'
import { IS_MOBILE_APP } from '@/constants'

const getUser = async (userId: string) => {
  const res = await request<{ user: UserResponse }>({
    endpoint: `/user/${userId}`,
    method: 'GET'
  })
  if (res.success) zodUserResponseValidator(res.data.user)
  return res
}

export const useGetUser = (userId: string | undefined) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'user', userId],
    queryFn: () => getUser(userId || ''),
    enabled: !!userId
  })
}

export const getOrgUserInfo = async (body: {
  userId?: string,
  subjectId?: string
}) => {
  const res = await request<OrgUserResponse>({
    endpoint: '/organization/roles',
    method: 'POST',
    body
  })
  if (res.success) zodOrgUserResponseValidator(res.data)
  return res
}

export const useGetOrgUserInfo = (data: {
  userId?: string,
  subjectId?: string
}, enabled = true) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'orgUserInfo', data],
    queryFn: () => getOrgUserInfo(data),
    enabled
  })
}

export const getMe = async (params?: {
  inviteCode?: string
  inviteToken?: string
  email?: string
}) => {
  let url = '/me'
  if (params) {
    const qp = new URLSearchParams()
    if (params.inviteToken) qp.append('invite_token', params.inviteToken)
    else if (params.inviteCode) qp.append('invite_code', params.inviteCode)
    if (params.email) qp.append('email', params.email)
    if (IS_MOBILE_APP) qp.append('isMobile', 'true')
    const qs = qp.toString()
    if (qs) url += `?${qs}`
  }
  const res = await request<MeResponse>({
    endpoint: url,
    method: 'GET'
  })
  if (res.success) zodMeResponseValidator(res.data)
  return res
}

export const deleteMe = async () => {
  return request({
    endpoint: '/me?client=fuzzy_watchdog',
    method: 'DELETE',
  })
}

const getMyAlertsIndividuals = async () => {
  const res = await request<UserAssignmentResponse[]>({
    endpoint: '/alert/assigned?mode=individuals',
    method: 'GET',
  })
  if (res.success) zodUserAssignmentResponseValidator(res.data)
  return res
}

export const useGetMyAlertsIndividuals = () => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'myAlertsIndividuals'],
    queryFn: () => getMyAlertsIndividuals(),
  })
}

/* -------- Authentication -------- */

const getMyIdentities = async () => {
  const res = await request<{ identities: UserAuthIdentity[] }>({
    endpoint: '/me/identities',
    method: 'GET',
  })
  if (res.success) zodUserAuthIdentityValidator(res.data.identities)
  return res
}

export const useGetMyIdentities = () => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'myIdentities'],
    queryFn: () => getMyIdentities(),
  })
}

export const createIdentity = async (body: {
  idToken: string,
  provider: AuthIdentityProvidersType
}) => {
  return request<any>({
    endpoint: '/me/identities',
    method: 'POST',
    body,
  })
}

export const removeIdentity = async (provider: AuthIdentityProvidersType) => {
  return request<any>({
    endpoint: `/me/identities/${encodeURIComponent(provider)}`,
    method: 'DELETE',
  })
}

export const setEmailPassword = async (body: {
  email: string
  password: string
}) => {
  return request<any>({
    endpoint: '/me/password',
    method: 'POST',
    body,
  })
}

const getMyMFA = async () => {
  const res = await request<{ enabled: boolean }>({
    endpoint: '/me/mfa',
    method: 'GET',
  })
  return res
}

export const useGetMyMFA = () => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'myMFA'],
    queryFn: () => getMyMFA(),
  })
}

export const setMyMFA = async (enabled: boolean) => {
  return request<any>({
    endpoint: '/me/mfa',
    method: 'PUT',
    body: { enabled },
  })
}

export const requestEmailChange = async (body: { newEmail: string }) => {
  return request<void>({
    endpoint: '/me/email/request',
    method: 'POST',
    body,
  })
}

export const confirmEmailChange = async (body: { newEmail: string; code: string }) => {
  return request<void>({
    endpoint: '/me/email/confirm',
    method: 'POST',
    body,
  })
}
